import { ChildProcess, spawn } from 'child_process';
import type { DispatchedJob } from '@whittlelabs/jobs';
import {
  renderPromptExecution,
  readPromptExecutionSpec,
  pickProviderHints,
  PROMPT_EXECUTION_STRATEGY,
} from '@whittlelabs/loom';
import type { CostHint, ExecutorCapability, ExecutorResponse, JobExecutor } from './types';
import { assertOutputValid } from './validate-output';

/**
 * Custom-script executor: spawns a shell command, pipes the rendered request as
 * JSON on stdin, reads `{ rawText, parsed?, usage? }` JSON from stdout.
 */
interface CustomScriptConfig {
  command: string;
  timeout: number;
  capabilityId: string;
}

const DEFAULT_TIMEOUT = 30_000;

interface ScriptOutput {
  rawText?: string;
  parsed?: Record<string, unknown>;
  usage?: ExecutorResponse['usage'];
}

class CustomScriptExecutor implements JobExecutor {
  readonly capability: ExecutorCapability;
  readonly strategies = [PROMPT_EXECUTION_STRATEGY];
  private readonly config: CustomScriptConfig;

  constructor(config: CustomScriptConfig) {
    this.config = config;
    this.capability = { id: config.capabilityId };
  }

  estimate(dispatch: DispatchedJob): CostHint {
    const spec = readPromptExecutionSpec(dispatch.inputs);
    return {
      inputTokens: spec.costCapHint?.estimatedInputTokens,
      outputTokens: spec.costCapHint?.estimatedOutputTokens,
    };
  }

  execute(dispatch: DispatchedJob, signal: AbortSignal): Promise<ExecutorResponse> {
    const { prompt, spec, artifacts } = renderPromptExecution(dispatch.inputs);
    const outputLabel = spec.outputLabel;
    const request = {
      prompt,
      outputSchema: spec.outputSchema,
      outputLabel,
      providerHints: pickProviderHints(spec, this.config.capabilityId),
      artifacts,
    };

    return new Promise<ExecutorResponse>((resolve, reject) => {
      let child: ChildProcess;
      try {
        child = spawn(this.config.command, [], {
          shell: true,
          stdio: ['pipe', 'pipe', 'pipe'],
        });
      } catch (err) {
        reject(err);
        return;
      }

      const stdoutChunks: Buffer[] = [];
      let settled = false;
      const settle = (fn: () => void): void => {
        if (settled) return;
        settled = true;
        fn();
      };

      child.stdout!.on('data', (chunk: Buffer) => stdoutChunks.push(chunk));
      child.stdin!.write(JSON.stringify(request));
      child.stdin!.end();

      const timer = setTimeout(() => {
        child.kill('SIGTERM');
        settle(() => reject(new Error(`Custom script timed out after ${this.config.timeout}ms`)));
      }, this.config.timeout);

      function onAbort(): void {
        child.kill('SIGTERM');
        settle(() => reject(new Error('Dispatch aborted')));
      }

      if (signal.aborted) {
        clearTimeout(timer);
        child.kill('SIGTERM');
        settle(() => reject(new Error('Dispatch aborted')));
        return;
      }
      signal.addEventListener('abort', onAbort, { once: true });

      child.on('error', (err: Error) => {
        clearTimeout(timer);
        signal.removeEventListener('abort', onAbort);
        settle(() => reject(err));
      });

      child.on('close', (code: number | null) => {
        clearTimeout(timer);
        signal.removeEventListener('abort', onAbort);
        const stdout = Buffer.concat(stdoutChunks).toString('utf-8');
        if (code !== 0) {
          settle(() => reject(new Error(`Custom script exited with code ${code}`)));
          return;
        }
        let parsed: ScriptOutput;
        try {
          parsed = JSON.parse(stdout) as ScriptOutput;
        } catch {
          settle(() =>
            reject(new Error(`Custom script returned non-JSON stdout: ${stdout.slice(0, 200)}`)),
          );
          return;
        }
        if (typeof parsed.rawText !== 'string') {
          settle(() => reject(new Error('Custom script output missing "rawText" string')));
          return;
        }
        const rawText = parsed.rawText;
        assertOutputValid(spec, parsed.parsed);
        settle(() =>
          resolve({
            outputs: [{ label: outputLabel, content: parsed.parsed ?? { raw: rawText } }],
            rawText,
            parsed: parsed.parsed,
            usage: parsed.usage,
          }),
        );
      });
    });
  }
}

export function createCustomScriptExecutor(
  config: Record<string, unknown>,
  capabilityId = 'custom-script',
): CustomScriptExecutor {
  const command = config.command;
  if (typeof command !== 'string' || command.length === 0) {
    throw new Error('CustomScriptExecutor requires a non-empty "command" string in config');
  }
  let timeout = DEFAULT_TIMEOUT;
  if (config.timeout !== undefined) {
    if (typeof config.timeout !== 'number' || config.timeout <= 0) {
      throw new Error('"timeout" must be a positive number');
    }
    timeout = config.timeout;
  }
  return new CustomScriptExecutor({ command, timeout, capabilityId });
}
